import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  Download,
  Filter,
  Check,
  Square,
  CheckSquare,
  ArrowLeft,
  BookOpen,
  List,
  FileDown,
  Search,
  X,
  Trash2,
  Brain,
  Lock,
  Pencil,
  Save
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getUserBibliographyEntries, deleteBibliographyEntry } from '../services/bibliography';
import { bibliographyAPI } from '../services/api';
import { canAccessFeature } from '../services/stripe';
import { FadeIn, StaggerChildren, StaggerItem } from '../components/motion';
import LoadingSkeleton from '../components/LoadingSkeleton';
import toast from 'react-hot-toast';
import { exportToBibliography } from '../utils/exportUtils';
import { Document, Packer, Paragraph, TextRun, AlignmentType } from 'docx';
import SEO from '../components/SEO';

const BibliographyPage = () => {
  const { currentUser, userDocument } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  useEffect(() => {
    fetchEntries();
  }, [currentUser]);

  const fetchEntries = async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      const result = await getUserBibliographyEntries(currentUser.uid, 100);

      if (result.success) {
        setEntries(result.entries);

        // Extract unique categories
        const uniqueCategories = [...new Set(result.entries.map(e => e.researchFocus))];
        setCategories(uniqueCategories);
      } else {
        toast.error('Failed to load source entries');
      }
    } catch (error) {
      console.error('Error fetching entries:', error);
      toast.error('Failed to load source entries');
    } finally {
      setLoading(false);
    }
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch =
      entry.citation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.narrative_overview.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.researchFocus.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === 'all' || entry.researchFocus === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const handleSelectAll = () => {
    if (selectedEntries.size === filteredEntries.length) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(filteredEntries.map(e => e.id)));
    }
  };

  const handleSelectEntry = (entryId) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const handleDeleteEntry = async (entryId, e) => {
    e.stopPropagation();

    if (window.confirm('Are you sure you want to delete this source? This action cannot be undone.')) {
      try {
        const result = await deleteBibliographyEntry(entryId);
        if (result.success) {
          setEntries(entries.filter(entry => entry.id !== entryId));
          setSelectedEntries(prev => {
            const newSet = new Set(prev);
            newSet.delete(entryId);
            return newSet;
          });
          toast.success('Source deleted successfully');
        } else {
          toast.error('Failed to delete source');
        }
      } catch (error) {
        console.error('Error deleting entry:', error);
        toast.error('Failed to delete source');
      }
    }
  };

  const handleEditEntry = (entry, e) => {
    e.stopPropagation();
    setEditingEntry(entry);
    setEditForm({
      author: entry.source_info?.author || '',
      title: entry.source_info?.title || '',
      publication: entry.source_info?.publication || '',
      year: entry.source_info?.year || '',
      key_arguments: entry.key_arguments || '',
      interesting_angles: entry.interesting_angles || '',
      perspective_value: entry.perspective_value || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    setIsSavingEdit(true);
    try {
      await bibliographyAPI.updateEntry(editingEntry.id, {
        source_info: {
          author: editForm.author,
          title: editForm.title,
          publication: editForm.publication,
          year: editForm.year,
        },
        key_arguments: editForm.key_arguments,
        interesting_angles: editForm.interesting_angles,
        perspective_value: editForm.perspective_value,
      });

      // Update the local state
      setEntries(entries.map(e => {
        if (e.id === editingEntry.id) {
          return {
            ...e,
            source_info: {
              author: editForm.author,
              title: editForm.title,
              publication: editForm.publication,
              year: editForm.year,
            },
            key_arguments: editForm.key_arguments,
            interesting_angles: editForm.interesting_angles,
            perspective_value: editForm.perspective_value,
          };
        }
        return e;
      }));

      toast.success('Entry updated successfully');
      setEditingEntry(null);
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error('Failed to update entry');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const exportAnnotatedBibliography = async () => {
    const selectedData = entries.filter(e => selectedEntries.has(e.id));

    if (selectedData.length === 0) {
      toast.error('Please select at least one entry to export');
      return;
    }

    try {
      await exportToBibliography(selectedData, 'word');
      toast.success(`Exported ${selectedData.length} entries as detailed source summary`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export sources');
    }
  };

  const exportReferencesList = async () => {
    const selectedData = entries.filter(e => selectedEntries.has(e.id));

    if (selectedData.length === 0) {
      toast.error('Please select at least one entry to export');
      return;
    }

    try {
      // Sort alphabetically by citation
      const sortedData = [...selectedData].sort((a, b) => a.citation.localeCompare(b.citation));

      // Create document
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            new Paragraph({
              children: [
                new TextRun({
                  text: "SOURCES",
                  bold: true,
                  size: 32,
                })
              ],
              alignment: AlignmentType.CENTER,
              spacing: {
                after: 480,
              }
            }),
            ...sortedData.map(entry =>
              new Paragraph({
                children: [
                  new TextRun({
                    text: entry.citation,
                    size: 24,
                  })
                ],
                spacing: {
                  after: 240,
                }
              })
            )
          ]
        }]
      });

      // Generate and download
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sources-list-${Date.now()}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${selectedData.length} sources`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export sources');
    }
  };

  const EntryCard = ({ entry, index }) => {
    const isSelected = selectedEntries.has(entry.id);

    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
      >
        <div
          className={`card-floating cursor-pointer transition-all ${
            isSelected ? 'ring-2 ring-primary bg-primary-50/50' : ''
          }`}
          onClick={() => handleSelectEntry(entry.id)}
        >
          <div className="flex items-start space-x-4">
            <motion.div
              className="pt-1"
              whileTap={{ scale: 0.85 }}
            >
              {isSelected ? (
                <CheckSquare className="w-5 h-5 text-primary" />
              ) : (
                <Square className="w-5 h-5 text-secondary-400" />
              )}
            </motion.div>

            <div className="flex-1">
              <div className="flex items-start justify-between mb-2">
                <span className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-xs font-medium">
                  {entry.researchFocus}
                </span>
                <div className="flex items-center space-x-1">
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => handleEditEntry(entry, e)}
                    className="p-1.5 text-secondary-400 hover:text-primary hover:bg-primary-50 rounded-lg transition-colors"
                    title="Edit source"
                  >
                    <Pencil className="w-4 h-4" />
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={(e) => handleDeleteEntry(entry.id, e)}
                    className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete source"
                  >
                    <Trash2 className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>

              <div className="font-semibold text-secondary-900 mb-2 line-clamp-3">
                {entry.citation}
              </div>

              <p className="text-secondary-600 text-sm line-clamp-2">
                {entry.narrative_overview}
              </p>

              <div className="mt-3 text-xs text-secondary-400">
                Added on {new Date(entry.createdAt?.toDate ? entry.createdAt.toDate() : entry.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-mesh flex items-center justify-center">
        <div className="text-center">
          <motion.div
            className="w-16 h-16 bg-gradient-to-br from-accent to-primary-900 rounded-2xl flex items-center justify-center mx-auto mb-4"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <FileText className="w-8 h-8 text-white" />
          </motion.div>
          <p className="text-secondary-600">Loading your sources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary-50/40 py-8">
      <SEO title="Sources" noIndex={true} />
      <div className="max-w-7xl mx-auto px-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-secondary-500 mb-6">
          <Link to="/dashboard" className="hover:text-secondary-900 transition-colors">Dashboard</Link>
          <span className="text-secondary-300">/</span>
          <span className="text-secondary-900 font-medium">Source library</span>
        </nav>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <div className="flex items-start gap-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-1 p-1.5 text-secondary-500 hover:text-secondary-900 hover:bg-secondary-100 rounded-md transition-colors"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-2xl lg:text-3xl font-semibold text-secondary-900 tracking-tight">
                Source library
              </h1>
              <p className="mt-1 text-sm text-secondary-500">
                Search, filter, and manage your imported sources.
              </p>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <FadeIn direction="up" delay={0.1}>
          <div className="glass-card p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* Left side - Search and Filter */}
              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
                {/* Search */}
                <div className="relative group">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary-400 group-focus-within:text-primary transition-colors" />
                  <input
                    type="text"
                    placeholder="Search entries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input pl-10 pr-4 py-2.5 w-full sm:w-64 focus:shadow-glow-sm"
                  />
                </div>

                {/* Category Filter */}
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="form-input py-2.5"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Right side - Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                {canAccessFeature(userDocument, 'topic_outline') ? (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      if (selectedEntries.size < 2) {
                        toast.error('Please select at least 2 entries to analyze');
                        return;
                      }
                      const selectedData = entries.filter(e => selectedEntries.has(e.id));
                      navigate('/analyze', { state: { selectedEntries: selectedData } });
                    }}
                    disabled={selectedEntries.size < 2}
                    className={`btn ${
                      selectedEntries.size >= 2 ? 'btn-primary' : 'btn-outline opacity-50 cursor-not-allowed'
                    }`}
                    title={selectedEntries.size < 2 ? 'Select at least 2 entries to analyze' : 'Generate topics and outlines'}
                  >
                    <Brain className="w-4 h-4 mr-2" />
                    Analyze Selected
                  </motion.button>
                ) : (
                  <div className="relative group">
                    <button
                      disabled
                      className="btn btn-outline opacity-50 cursor-not-allowed"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Analyze Selected
                    </button>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-primary-900 text-white text-sm rounded-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                      Idea & Outline Generator requires Researcher plan
                      <br />
                      <Link to="/pricing" className="text-primary-300 hover:underline underline">
                        Upgrade now →
                      </Link>
                    </div>
                  </div>
                )}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={exportReferencesList}
                  disabled={selectedEntries.size === 0}
                  className={`btn btn-outline ${
                    selectedEntries.size === 0 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <List className="w-4 h-4 mr-2" />
                  Export Sources
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={exportAnnotatedBibliography}
                  disabled={selectedEntries.size === 0}
                  className={`btn btn-outline ${
                    selectedEntries.size === 0 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Export Detailed
                </motion.button>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Selection Info */}
        <FadeIn direction="up" delay={0.15}>
          <div className="bg-secondary-50/50 rounded-xl p-4 mb-6 border border-secondary-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center space-x-2 text-secondary-900 hover:text-primary transition-colors"
                >
                  {selectedEntries.size === filteredEntries.length && filteredEntries.length > 0 ? (
                    <CheckSquare className="w-5 h-5" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                  <span className="font-medium">Select All</span>
                </button>
                <span className="text-secondary-500">
                  {selectedEntries.size} of {filteredEntries.length} entries selected
                </span>
              </div>

              <AnimatePresence>
                {selectedEntries.size > 0 && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={() => setSelectedEntries(new Set())}
                    className="text-secondary-400 hover:text-secondary-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </div>
        </FadeIn>

        {/* Entries Grid */}
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {[...Array(6)].map((_, i) => (
              <LoadingSkeleton key={i} variant="entry-card" />
            ))}
          </div>
        ) : filteredEntries.length === 0 ? (
          <FadeIn direction="up">
            <div className="border border-[#e5e7eb] rounded-lg shadow-card bg-white text-center py-16 px-6">
              {(searchTerm || selectedCategory !== 'all') ? (
                <>
                  <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-4">
                    <Search className="w-8 h-8 text-secondary-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                    No matching sources
                  </h3>
                  <p className="text-sm text-secondary-500 mb-6 max-w-md mx-auto">
                    {searchTerm
                      ? <>No sources match <span className="font-medium">"{searchTerm}"</span>. Try a different search or clear your filters.</>
                      : 'No sources match the selected category. Try a different filter.'}
                  </p>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                    }}
                    className="text-sm text-primary font-medium hover:underline"
                  >
                    Clear filters
                  </button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                    Your library is empty
                  </h3>
                  <p className="text-sm text-secondary-500 mb-6 max-w-md mx-auto">
                    Upload a PDF, paste a URL or DOI, or subscribe to a research feed to start building your source library.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => navigate('/create')}
                    className="inline-flex items-center gap-2 bg-primary text-white hover:bg-primary-700 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors"
                  >
                    Add your first source
                  </motion.button>
                </>
              )}
            </div>
          </FadeIn>
        ) : (
          <AnimatePresence>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {filteredEntries.map((entry, index) => (
                <EntryCard key={entry.id} entry={entry} index={index} />
              ))}
            </div>
          </AnimatePresence>
        )}

        {/* Edit Entry Modal */}
        <AnimatePresence>
          {editingEntry && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
              onClick={() => setEditingEntry(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-secondary-900">Edit Source Entry</h2>
                  <button
                    onClick={() => setEditingEntry(null)}
                    className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Source Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">Author</label>
                      <input
                        type="text"
                        value={editForm.author}
                        onChange={(e) => setEditForm({ ...editForm, author: e.target.value })}
                        className="form-input w-full"
                        placeholder="Author name(s)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 mb-1">Year</label>
                      <input
                        type="text"
                        value={editForm.year}
                        onChange={(e) => setEditForm({ ...editForm, year: e.target.value })}
                        className="form-input w-full"
                        placeholder="Publication year"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="form-input w-full"
                      placeholder="Document title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Publication</label>
                    <input
                      type="text"
                      value={editForm.publication}
                      onChange={(e) => setEditForm({ ...editForm, publication: e.target.value })}
                      className="form-input w-full"
                      placeholder="Journal, publisher, or website"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Key Arguments</label>
                    <textarea
                      value={editForm.key_arguments}
                      onChange={(e) => setEditForm({ ...editForm, key_arguments: e.target.value })}
                      className="form-input w-full h-28 resize-none"
                      placeholder="Main claims or arguments..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Interesting Angles</label>
                    <textarea
                      value={editForm.interesting_angles}
                      onChange={(e) => setEditForm({ ...editForm, interesting_angles: e.target.value })}
                      className="form-input w-full h-28 resize-none"
                      placeholder="Surprising or counterintuitive insights..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Perspective Value</label>
                    <textarea
                      value={editForm.perspective_value}
                      onChange={(e) => setEditForm({ ...editForm, perspective_value: e.target.value })}
                      className="form-input w-full h-28 resize-none"
                      placeholder="Unique perspective or lens this source brings..."
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-secondary-200">
                  <button
                    onClick={() => setEditingEntry(null)}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSaveEdit}
                    disabled={isSavingEdit}
                    className="btn btn-primary flex items-center space-x-2"
                  >
                    {isSavingEdit ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>Save Changes</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* AI Analysis Feature Highlight */}
        <FadeIn direction="up" delay={0.2}>
          <div className="mt-12 card-floating bg-gradient-to-br from-accent-50/50 to-primary-50/30">
            <div className="flex items-start space-x-4">
              <motion.div
                className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center"
                whileHover={{ rotate: 5, scale: 1.05 }}
              >
                <Brain className="w-6 h-6 text-primary-600" />
              </motion.div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">
                  NEW: AI Writing Assistant
                </h3>
                <ul className="text-secondary-600 space-y-1 list-disc ml-4">
                  <li>Generate content ideas & detailed outlines from your selected entries</li>
                  <li>AI synthesizes your sources to suggest compelling angles</li>
                  <li>Maps evidence to outline sections automatically</li>
                  <li>Export publication-ready outlines to Word</li>
                </ul>
                <AnimatePresence>
                  {selectedEntries.size >= 2 && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 p-3 bg-primary-50 rounded-xl"
                    >
                      <p className="text-primary-700 font-medium flex items-center">
                        <Brain className="w-4 h-4 mr-2" />
                        You have {selectedEntries.size} entries selected - perfect for analysis!
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => {
                            const selectedData = entries.filter(e => selectedEntries.has(e.id));
                            navigate('/analyze', { state: { selectedEntries: selectedData } });
                          }}
                          className="ml-auto btn btn-primary btn-sm"
                        >
                          Try It Now
                        </motion.button>
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </div>
  );
};

export default BibliographyPage;
